// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import { buildEvent, formatAmount } from '../../lib/eventLog.js';
import { hapticSuccess, hapticLight } from '../../native/haptics.js';
import { AccountPicker } from './AccountPicker.js';
import { CloseIcon, ChevronDownIcon } from './icons.js';
import {
  getRecentPayees,
  matchPayees,
  resolveDefaultFromAccount,
  lastUsedExpenseAccount,
} from './quickEntryData.js';
import type { TransactionPostedPayload } from '../../types.js';

interface QuickEntryProps {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
}

// DECISION (Phase 4 — the single most important mobile screen): built for speed.
// It opens straight to a large amount readout over a custom numeric keypad (no
// keyboard round-trip, guaranteed big targets). "From" defaults to the user's
// cash account, "To" is inferred from the payee's history, and the date defaults
// to today — so the common case (cash purchase, known payee) is: type amount →
// tap payee chip → Save, in well under ten seconds. Everything writes to the
// local event log immediately, so it works fully offline (Phase 10).
export function QuickEntry({ worker, keyData }: QuickEntryProps) {
  const { t } = useTranslation(['transactions', 'accounting', 'common', 'errors']);
  const { state } = useApp();
  const { ledger, appSettings } = state;
  const navigate = useNavigate();
  const encryptAndPush = useEncryptAndPush(worker, keyData);

  const defaultFrom = useMemo(
    () => resolveDefaultFromAccount(ledger, appSettings.defaultQuickEntryAccount),
    [ledger, appSettings.defaultQuickEntryAccount],
  );
  const recentPayees = useMemo(() => getRecentPayees(ledger, 6), [ledger]);

  // Amount is accumulated in minor units (calculator style): pressing 4,7,5 with
  // a 2-decimal currency yields 4.75. No decimal key needed.
  const [minor, setMinor] = useState(0);
  const [fromId, setFromId] = useState<string | null>(defaultFrom?.id ?? null);
  const [toId, setToId] = useState<string | null>(null);
  const [payee, setPayee] = useState('');
  const [memo, setMemo] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [showFromPicker, setShowFromPicker] = useState(false);
  const [showToPicker, setShowToPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fromAccount = fromId ? ledger.accounts.get(fromId) : undefined;
  const toAccount = toId ? ledger.accounts.get(toId) : undefined;
  const currency = fromAccount?.currency ?? ledger.settings.homeCurrency;

  const payeeMatches = useMemo(
    () => (payee.trim() ? matchPayees(ledger, payee, 4) : []),
    [ledger, payee],
  );

  function press(digit: number) {
    void hapticLight();
    // Cap to avoid overflow on absurd inputs.
    setMinor((m) => (m > 1_000_000_000_00 ? m : m * 10 + digit));
  }
  function backspace() {
    void hapticLight();
    setMinor((m) => Math.floor(m / 10));
  }

  // Selecting a recent payee also pre-fills the likely category.
  function pickPayee(name: string, toAccountId: string | null) {
    setPayee(name);
    if (toAccountId && ledger.accounts.get(toAccountId)) setToId(toAccountId);
    else if (!toId) setToId(lastUsedExpenseAccount(ledger));
  }

  const canSave = minor > 0 && !!fromId && !!toId && fromId !== toId && !saving;

  async function save(addAnother: boolean) {
    setError('');
    if (!fromId || !toId) {
      setError(t('quickEntry.chooseBothAccounts'));
      return;
    }
    if (fromId === toId) {
      setError(t('quickEntry.sameAccount'));
      return;
    }
    if (minor <= 0) {
      setError(t('quickEntry.enterAmount'));
      return;
    }
    setSaving(true);
    try {
      // Double-entry: credit the source (asset) account, debit the destination.
      const payload: TransactionPostedPayload = {
        transactionId: crypto.randomUUID(),
        date,
        dateEntered: new Date().toISOString(),
        description: payee.trim() || t('quickEntry.title'),
        notes: memo.trim(),
        currency,
        splits: [
          {
            splitId: crypto.randomUUID(),
            accountId: fromId,
            amount: -minor,
            quantity: -minor,
            reconcileState: 'n',
            reconcileDate: null,
            memo: '',
            action: '',
          },
          {
            splitId: crypto.randomUUID(),
            accountId: toId,
            amount: minor,
            quantity: minor,
            reconcileState: 'n',
            reconcileDate: null,
            memo: memo.trim(),
            action: '',
          },
        ],
      };
      await encryptAndPush(await buildEvent('TransactionPosted', payload, ledger.lastSequence + 1));
      await hapticSuccess();

      if (addAnother) {
        // Keep From/To/date for rapid receipt-stack entry; clear the rest.
        setMinor(0);
        setPayee('');
        setMemo('');
      } else {
        navigate(-1);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('quickEntry.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-bg flex flex-col">
      {/* Header */}
      <header className="shrink-0 bg-bg-surface border-b border-border pt-safe">
        <div className="h-12 flex items-center justify-between px-2">
          <button
            onClick={() => navigate(-1)}
            aria-label={t('common:actions.cancel')}
            className="w-10 h-10 flex items-center justify-center text-text-muted active:text-text"
          >
            <CloseIcon size={20} />
          </button>
          <span className="text-md font-semibold text-text">{t('quickEntry.newTransaction')}</span>
          <button
            onClick={() => void save(false)}
            disabled={!canSave}
            className="px-3 h-8 bg-accent text-white text-sm font-medium disabled:opacity-40 active:bg-accent-hover"
          >
            {saving ? t('common:actions.saving') : t('common:actions.save')}
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto native-scroll">
        {/* Amount readout */}
        <div className="py-6 text-center">
          <div className="text-4xl font-mono font-medium text-text tabular-nums">
            {formatAmount(minor, currency)}
          </div>
          <div className="text-xs text-text-subtle mt-1 uppercase tracking-widest">{currency}</div>
        </div>

        <div className="px-4 space-y-4 pb-4">
          {/* Payee */}
          <div>
            <label className="block text-xs text-text-muted uppercase tracking-wide mb-1">{t('quickEntry.payee')}</label>
            <input
              value={payee}
              onChange={(e) => setPayee(e.target.value)}
              placeholder={t('quickEntry.payeePrompt')}
              className="w-full bg-bg-surface border border-border text-text text-md px-3 h-11 focus:border-accent focus:outline-none placeholder-text-subtle"
            />
            {/* Autocomplete matches while typing */}
            {payeeMatches.length > 0 && payee.trim() !== '' && (
              <div className="mt-1 border border-border bg-bg-surface">
                {payeeMatches.map((p) => (
                  <button
                    key={p.name}
                    onClick={() => pickPayee(p.name, p.toAccountId)}
                    className="w-full text-left px-3 py-2 text-md text-text border-b border-border last:border-0 active:bg-bg-elevated"
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            )}
            {/* Recent payee quick-tap chips */}
            {payee.trim() === '' && recentPayees.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {recentPayees.map((p) => (
                  <button
                    key={p.name}
                    onClick={() => pickPayee(p.name, p.toAccountId)}
                    className="px-3 h-8 bg-bg-surface border border-border text-sm text-text-muted active:border-accent active:text-text"
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* From / To */}
          <div className="grid grid-cols-2 gap-3">
            <AccountField
              label={t('quickEntry.from')}
              value={fromAccount?.name ?? t('quickEntry.select')}
              onClick={() => setShowFromPicker(true)}
            />
            <AccountField
              label={t('quickEntry.to')}
              value={toAccount?.name ?? t('quickEntry.select')}
              onClick={() => setShowToPicker(true)}
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-xs text-text-muted uppercase tracking-wide mb-1">{t('form.date')}</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-bg-surface border border-border text-text text-md px-3 h-11 focus:border-accent focus:outline-none"
            />
          </div>

          {/* Memo */}
          <div>
            <label className="block text-xs text-text-muted uppercase tracking-wide mb-1">{t('quickEntry.memoOptional')}</label>
            <input
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder={t('quickEntry.notePlaceholder')}
              className="w-full bg-bg-surface border border-border text-text text-md px-3 h-11 focus:border-accent focus:outline-none placeholder-text-subtle"
            />
          </div>

          {error && <p className="text-sm text-negative">{error}</p>}
        </div>
      </div>

      {/* Numeric keypad + actions — pinned to the bottom, thumb-reachable */}
      <div className="shrink-0 bg-bg-surface border-t border-border pb-safe">
        <Keypad onPress={press} onBackspace={backspace} />
        <div className="flex gap-2 px-3 pb-3">
          <button
            onClick={() => void save(true)}
            disabled={!canSave}
            className="flex-1 h-12 border border-border text-md text-text-muted disabled:opacity-40 active:bg-bg-elevated"
          >
            {t('quickEntry.saveAddAnother')}
          </button>
          <button
            onClick={() => void save(false)}
            disabled={!canSave}
            className="flex-1 h-12 bg-accent text-white text-md font-medium disabled:opacity-40 active:bg-accent-hover"
          >
            {saving ? t('common:actions.saving') : t('common:actions.save')}
          </button>
        </div>
      </div>

      <AccountPicker
        open={showFromPicker}
        title={t('quickEntry.fromAccount')}
        onClose={() => setShowFromPicker(false)}
        onSelect={setFromId}
      />
      <AccountPicker
        open={showToPicker}
        title={t('quickEntry.toAccount')}
        onClose={() => setShowToPicker(false)}
        onSelect={setToId}
      />
    </div>
  );
}

function AccountField({ label, value, onClick }: { label: string; value: string; onClick: () => void }) {
  return (
    <div>
      <label className="block text-xs text-text-muted uppercase tracking-wide mb-1">{label}</label>
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between gap-1 bg-bg-surface border border-border text-text text-md px-3 h-11 active:border-accent"
      >
        <span className="truncate">{value}</span>
        <ChevronDownIcon size={16} className="text-text-muted shrink-0" />
      </button>
    </div>
  );
}

// Custom 3x4 numeric keypad. Large targets, tabular digits, no decimal key (the
// amount accumulates in minor units).
function Keypad({ onPress, onBackspace }: { onPress: (d: number) => void; onBackspace: () => void }) {
  const { t } = useTranslation('common');
  const keys = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  return (
    <div className="grid grid-cols-3 gap-px bg-border">
      {keys.map((k) => (
        <KeypadKey key={k} onClick={() => onPress(k)}>
          {k}
        </KeypadKey>
      ))}
      <div className="bg-bg-surface" />
      <KeypadKey onClick={() => onPress(0)}>0</KeypadKey>
      <KeypadKey onClick={onBackspace} aria-label={t('actions.delete')}>
        ⌫
      </KeypadKey>
    </div>
  );
}

function KeypadKey({
  children,
  onClick,
  ...rest
}: { children: React.ReactNode; onClick: () => void } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      onClick={onClick}
      className="h-14 bg-bg-surface text-2xl font-mono text-text active:bg-bg-elevated"
      {...rest}
    >
      {children}
    </button>
  );
}
